import { RefObject } from 'react';
import { AbstractSchema, InferTypeFromSchema } from '../../common/args';
import { IFrameProps } from '../../common/types';
import {
  Overridable,
  makeOverridableFactory,
} from '../../common/utils/make-overridable-factory';
import { waitForElement } from '../../common/utils/wait-for-element';
import { getPortalId } from '../../common/utils/get-portal-id';
import { Capability } from '../../capabilities';
import { FunctionsMap } from './internal/functions-map';
import {
  ComponentDefinition,
  ComponentName,
  QueryParamsResolver,
  WithGetters,
} from './nav-component-definition';

type SourceState = 'none' | 'src' | 'path';

interface RequiredFlags {
  hasCapabilities: boolean;
  hasPlatformName: boolean;
  hasInitialProps: boolean;
}

type InitialFlags = {
  hasCapabilities: false;
  hasPlatformName: false;
  hasInitialProps: false;
};

type SetFlag<
  Flags extends RequiredFlags,
  Key extends keyof RequiredFlags,
> = Omit<Flags, Key> & Record<Key, true>;

type ResetFlag<
  Flags extends RequiredFlags,
  Key extends keyof RequiredFlags,
> = Omit<Flags, Key> & Record<Key, false>;

type CallbackResolvable<
  Result,
  ParamsSchema extends AbstractSchema | undefined,
> = ParamsSchema extends undefined
  ? () => Result
  : (params: InferTypeFromSchema<ParamsSchema>) => Result;

type AsyncCallbackResolvable<
  Result,
  ParamsSchema extends AbstractSchema | undefined,
> = ParamsSchema extends undefined
  ? () => Result | Promise<Result>
  : (params: InferTypeFromSchema<ParamsSchema>) => Result | Promise<Result>;

export type Resolvable<T, ParamsSchema extends AbstractSchema | undefined> =
  | T
  | CallbackResolvable<T, ParamsSchema>;

export type AsyncResolvable<
  T,
  ParamsSchema extends AbstractSchema | undefined,
> = T | AsyncCallbackResolvable<T, ParamsSchema>;

type AnyResolvable<T> = Resolvable<T, any>;
type AnyAsyncResolvable<T> = AsyncResolvable<T, any>;

const isInstanceOfCallbackResolvable = <
  Result,
  ParamsSchema extends AbstractSchema | undefined,
>(
  resolvable:
    | Resolvable<Result, ParamsSchema>
    | AsyncResolvable<Result, ParamsSchema>
): resolvable is
  | CallbackResolvable<Result, ParamsSchema>
  | AsyncCallbackResolvable<Result, ParamsSchema> =>
  typeof resolvable === 'function';

const resolve = <Result, ParamsSchema extends AbstractSchema | undefined>(
  resolvable: AnyResolvable<Result>,
  params: InferTypeFromSchema<ParamsSchema>
) =>
  isInstanceOfCallbackResolvable(resolvable) ? resolvable(params) : resolvable;

type RenderTargetElement = HTMLElement | null;
type MutableComponentDefinition = Partial<ComponentDefinition<any, any>>;

// -- CDefBuilder conditional member interfaces --

interface WithSourceSetters<
  DefParams extends AbstractSchema | undefined,
  PS extends AbstractSchema | undefined,
  FS extends FunctionsMap | undefined,
  Flags extends RequiredFlags,
> {
  setPath(path: string): CDefBuilder<DefParams, PS, FS, 'path', Flags>;
  /** @deprecated Use setPath instead */
  setSrc(src: string): CDefBuilder<DefParams, PS, FS, 'src', Flags>;
}

interface WithQueryParamsSetter<
  DefParams extends AbstractSchema | undefined,
  PS extends AbstractSchema | undefined,
  FS extends FunctionsMap | undefined,
  Flags extends RequiredFlags,
> {
  setQueryParams(
    qp: AsyncResolvable<Record<string, string>, DefParams>
  ): CDefBuilder<DefParams, PS, FS, 'path', Flags>;
}

interface WithInitialPropsSetter<
  DefParams extends AbstractSchema | undefined,
  PS extends AbstractSchema | undefined,
  FS extends FunctionsMap | undefined,
  Src extends SourceState,
  Flags extends RequiredFlags,
> {
  setInitialProps(
    props: Resolvable<InferTypeFromSchema<PS>, DefParams>
  ): CDefBuilder<DefParams, PS, FS, Src, SetFlag<Flags, 'hasInitialProps'>>;
}

type BuildResult<
  DefParams extends AbstractSchema | undefined,
  PS extends AbstractSchema | undefined,
  FS extends FunctionsMap | undefined,
> = DefParams extends AbstractSchema
  ? WithGetters<
      Overridable<
        (params: InferTypeFromSchema<DefParams>) => ComponentDefinition<PS, FS>
      >
    >
  : WithGetters<Overridable<() => ComponentDefinition<PS, FS>>>;

interface WithBuild<
  DefParams extends AbstractSchema | undefined,
  PS extends AbstractSchema | undefined,
  FS extends FunctionsMap | undefined,
> {
  build(): BuildResult<DefParams, PS, FS>;
}

// -- CDefBuilder always-available member interfaces --

interface AlwaysAvailableMethods<
  DefParams extends AbstractSchema | undefined,
  PS extends AbstractSchema | undefined,
  FS extends FunctionsMap | undefined,
  Src extends SourceState,
  Flags extends RequiredFlags,
> {
  setCapabilityDefs(
    capabilityDefs: ReadonlyArray<Capability>
  ): CDefBuilder<DefParams, PS, FS, Src, SetFlag<Flags, 'hasCapabilities'>>;

  setPlatformName(
    name: string
  ): CDefBuilder<DefParams, PS, FS, Src, SetFlag<Flags, 'hasPlatformName'>>;

  setIframeProps(
    iframeProps: Resolvable<IFrameProps, DefParams>
  ): CDefBuilder<DefParams, PS, FS, Src, Flags>;

  setRenderTarget(
    target: string | AsyncResolvable<RenderTargetElement, DefParams>
  ): CDefBuilder<DefParams, PS, FS, Src, Flags>;

  setScrolling(scrolling: boolean): CDefBuilder<DefParams, PS, FS, Src, Flags>;

  setTags(
    tags: ReadonlyArray<string>
  ): CDefBuilder<DefParams, PS, FS, Src, Flags>;

  setEnableHubless(enable: boolean): CDefBuilder<DefParams, PS, FS, Src, Flags>;

  setRef(
    ref: Resolvable<
      RefObject<HTMLIFrameElement | null | undefined> | null,
      DefParams
    >
  ): CDefBuilder<DefParams, PS, FS, Src, Flags>;

  dangerouslySetLoadInstantly(
    next: Resolvable<boolean, DefParams>
  ): CDefBuilder<DefParams, PS, FS, Src, Flags>;

  setInitiallyOpen(
    open: Resolvable<boolean, DefParams>
  ): CDefBuilder<DefParams, PS, FS, Src, Flags>;

  setInitiallyFullscreen(
    fullscreen: boolean
  ): CDefBuilder<DefParams, PS, FS, Src, Flags>;
}

interface SchemaSettingMethods<
  DefParams extends AbstractSchema | undefined,
  PS extends AbstractSchema | undefined,
  FS extends FunctionsMap | undefined,
  Src extends SourceState,
  Flags extends RequiredFlags,
> {
  setDefinitionParams<NewDefParams extends AbstractSchema>(
    schema: NewDefParams
  ): CDefBuilder<NewDefParams, PS, FS, Src, Flags>;

  setPropsSchema<NewPS extends AbstractSchema>(
    schema: NewPS
  ): CDefBuilder<
    DefParams,
    NewPS,
    FS,
    Src,
    ResetFlag<Flags, 'hasInitialProps'>
  >;

  setFunctionsSchema<NewFS extends FunctionsMap>(
    functions: NewFS
  ): CDefBuilder<DefParams, PS, NewFS, Src, Flags>;
}

// -- CDefBuilder: determines build readiness --

type IsBuildReady<
  PS extends AbstractSchema | undefined,
  Src extends SourceState,
  Flags extends RequiredFlags,
> = Src extends 'src' | 'path'
  ? Flags['hasCapabilities'] extends true
    ? Flags['hasPlatformName'] extends true
      ? PS extends AbstractSchema
        ? Flags['hasInitialProps'] extends true
          ? true
          : false
        : true
      : false
    : false
  : false;

export type CDefBuilder<
  DefParams extends AbstractSchema | undefined = undefined,
  PS extends AbstractSchema | undefined = undefined,
  FS extends FunctionsMap | undefined = undefined,
  Src extends SourceState = 'none',
  Flags extends RequiredFlags = InitialFlags,
> = AlwaysAvailableMethods<DefParams, PS, FS, Src, Flags> &
  SchemaSettingMethods<DefParams, PS, FS, Src, Flags> &
  (Src extends 'none' ? WithSourceSetters<DefParams, PS, FS, Flags> : {}) &
  (Src extends 'path' ? WithQueryParamsSetter<DefParams, PS, FS, Flags> : {}) &
  (PS extends AbstractSchema
    ? WithInitialPropsSetter<DefParams, PS, FS, Src, Flags>
    : {}) &
  (IsBuildReady<PS, Src, Flags> extends true
    ? WithBuild<DefParams, PS, FS>
    : {});

// -- DefinitionConstruct: internal builder state --

interface DefinitionConstruct {
  name: ComponentName;
  source: { type: 'path'; path: string } | { type: 'src'; src: string } | null;
  definitionParamsSchema: AbstractSchema | undefined;
  propsSchema: AbstractSchema | undefined;
  initialProps: AnyResolvable<unknown> | undefined;
  functionsSchema: FunctionsMap | undefined;
  capabilities: ReadonlyArray<string> | null;
  capabilityDefs: ReadonlyArray<Capability>;
  queryParams: AnyAsyncResolvable<Record<string, string>> | undefined;
  iframeProps: AnyResolvable<IFrameProps> | undefined;
  renderTarget: string | AnyAsyncResolvable<RenderTargetElement> | undefined;
  scrolling: boolean | undefined;
  tags: ReadonlyArray<string> | undefined;
  enableHubless: boolean | undefined;
  ref:
    | AnyResolvable<RefObject<HTMLIFrameElement | null | undefined> | null>
    | undefined;
  loadInstantly: AnyResolvable<boolean> | undefined;
  initiallyOpen: AnyResolvable<boolean> | undefined;
  initiallyFullscreen: boolean | undefined;
  platformName: string | null;
}

type ValidatedConstruct = {
  [K in keyof DefinitionConstruct]: Exclude<DefinitionConstruct[K], null>;
};

const REQUIRED_FIELD_SETTERS: Partial<
  Record<keyof DefinitionConstruct, string>
> = {
  source: 'setPath() or setSrc()',
  capabilities: 'setCapabilityDefs()',
  platformName: 'setPlatformName()',
};

function assertConstructValid(
  construct: DefinitionConstruct,
  requiredFields: ReadonlyArray<keyof DefinitionConstruct>
): asserts construct is ValidatedConstruct {
  for (const field of requiredFields) {
    if (construct[field] === null) {
      const setter = REQUIRED_FIELD_SETTERS[field] ?? `${field}`;
      throw new Error(
        `ComponentDefinitionBuilder: ${setter} is required for "${construct.name}".`
      );
    }
  }
  if (
    construct.propsSchema !== undefined &&
    construct.initialProps === undefined
  ) {
    throw new Error(
      `ComponentDefinitionBuilder: "initialProps" is required when "propsSchema" is set for "${construct.name}". Call setInitialProps().`
    );
  }
}

const resolveRenderTarget = (
  target: string | AsyncResolvable<HTMLElement | null, any> | undefined,
  params: any
): (() => Promise<HTMLElement | null> | HTMLElement | null) | undefined => {
  if (target === undefined) {
    return undefined;
  }
  if (typeof target === 'string') {
    return () => waitForElement(target);
  }
  if (typeof target === 'function') {
    return () => target(params);
  }
  return () => target as HTMLElement | null;
};

const resolveQueryParams = (
  qp: unknown,
  params: any
): QueryParamsResolver | undefined => {
  if (qp === undefined) {
    return undefined;
  }
  if (typeof qp !== 'function') {
    return qp as Record<string, string>;
  }
  const fn = qp as (...args: any[]) => any;
  return () => Promise.resolve(fn(params));
};

const replacePortalId = (value: string): string => {
  const portalId = getPortalId();
  if (portalId === undefined) {
    return value;
  }
  return value.replace(':portalId', String(portalId));
};

// -- ComponentDefinitionBuilder class --

export class ComponentDefinitionBuilder<
  DefParams extends AbstractSchema | undefined = undefined,
  PS extends AbstractSchema | undefined = undefined,
  FS extends FunctionsMap | undefined = undefined,
  Src extends SourceState = 'none',
  Flags extends RequiredFlags = InitialFlags,
> {
  private static readonly REQUIRED_FIELDS: ReadonlyArray<
    keyof DefinitionConstruct
  > = ['source', 'capabilities', 'platformName'];

  private construct: DefinitionConstruct;

  private constructor(construct: DefinitionConstruct) {
    this.construct = construct;
  }

  static builder(
    name: ComponentName
  ): CDefBuilder<undefined, undefined, undefined, 'none', InitialFlags> {
    return new ComponentDefinitionBuilder({
      name,
      source: null,
      definitionParamsSchema: undefined,
      propsSchema: undefined,
      initialProps: undefined,
      functionsSchema: undefined,
      capabilities: null,
      capabilityDefs: [],
      queryParams: undefined,
      iframeProps: undefined,
      renderTarget: undefined,
      scrolling: undefined,
      tags: undefined,
      enableHubless: undefined,
      ref: undefined,
      loadInstantly: undefined,
      initiallyOpen: undefined,
      initiallyFullscreen: undefined,
      platformName: null,
    }) as any;
  }

  setPath(path: string): CDefBuilder<DefParams, PS, FS, 'path', Flags> {
    this.construct.source = { type: 'path', path };
    return this as any;
  }

  /** @deprecated Use setPath instead */
  setSrc(src: string): CDefBuilder<DefParams, PS, FS, 'src', Flags> {
    this.construct.source = { type: 'src', src };
    return this as any;
  }

  setCapabilityDefs(
    capabilityDefs: ReadonlyArray<Capability>
  ): CDefBuilder<DefParams, PS, FS, Src, SetFlag<Flags, 'hasCapabilities'>> {
    this.construct.capabilities = capabilityDefs.map((c) => c.getName());
    this.construct.capabilityDefs = capabilityDefs;
    return this as any;
  }

  setPlatformName(
    name: string
  ): CDefBuilder<DefParams, PS, FS, Src, SetFlag<Flags, 'hasPlatformName'>> {
    this.construct.platformName = name;
    return this as any;
  }

  setQueryParams(
    qp: AsyncResolvable<Record<string, string>, DefParams>
  ): CDefBuilder<DefParams, PS, FS, 'path', Flags> {
    this.construct.queryParams = qp;
    return this as any;
  }

  setDefinitionParams<NewDefParams extends AbstractSchema>(
    schema: NewDefParams
  ): CDefBuilder<NewDefParams, PS, FS, Src, Flags> {
    this.construct.definitionParamsSchema = schema;
    return this as any;
  }

  setPropsSchema<NewPS extends AbstractSchema>(
    schema: NewPS
  ): CDefBuilder<
    DefParams,
    NewPS,
    FS,
    Src,
    ResetFlag<Flags, 'hasInitialProps'>
  > {
    this.construct.propsSchema = schema;
    this.construct.initialProps = undefined;
    return this as any;
  }

  setInitialProps(
    props: Resolvable<InferTypeFromSchema<PS>, DefParams>
  ): CDefBuilder<DefParams, PS, FS, Src, SetFlag<Flags, 'hasInitialProps'>> {
    this.construct.initialProps = props;
    return this as any;
  }

  setFunctionsSchema<NewFS extends FunctionsMap>(
    functions: NewFS
  ): CDefBuilder<DefParams, PS, NewFS, Src, Flags> {
    this.construct.functionsSchema = functions;
    return this as any;
  }

  setIframeProps(
    iframeProps: Resolvable<IFrameProps, DefParams>
  ): CDefBuilder<DefParams, PS, FS, Src, Flags> {
    this.construct.iframeProps = iframeProps;
    return this as any;
  }

  setRenderTarget(
    target: string | AsyncResolvable<RenderTargetElement, DefParams>
  ): CDefBuilder<DefParams, PS, FS, Src, Flags> {
    this.construct.renderTarget = target;
    return this as any;
  }

  setScrolling(scrolling: boolean): CDefBuilder<DefParams, PS, FS, Src, Flags> {
    this.construct.scrolling = scrolling;
    return this as any;
  }

  setTags(
    tags: ReadonlyArray<string>
  ): CDefBuilder<DefParams, PS, FS, Src, Flags> {
    this.construct.tags = tags;
    return this as any;
  }

  setEnableHubless(
    enable: boolean
  ): CDefBuilder<DefParams, PS, FS, Src, Flags> {
    this.construct.enableHubless = enable;
    return this as any;
  }

  setRef(
    ref: Resolvable<
      RefObject<HTMLIFrameElement | null | undefined> | null,
      DefParams
    >
  ): CDefBuilder<DefParams, PS, FS, Src, Flags> {
    this.construct.ref = ref;
    return this as any;
  }

  dangerouslySetLoadInstantly(
    next: Resolvable<boolean, DefParams>
  ): CDefBuilder<DefParams, PS, FS, Src, Flags> {
    this.construct.loadInstantly = next;
    return this as any;
  }

  setInitiallyOpen(
    open: Resolvable<boolean, DefParams>
  ): CDefBuilder<DefParams, PS, FS, Src, Flags> {
    this.construct.initiallyOpen = open;
    return this as any;
  }

  setInitiallyFullscreen(
    fullscreen: boolean
  ): CDefBuilder<DefParams, PS, FS, Src, Flags> {
    this.construct.initiallyFullscreen = fullscreen;
    return this as any;
  }

  build(): BuildResult<DefParams, PS, FS> {
    const construct = { ...this.construct };
    assertConstructValid(construct, ComponentDefinitionBuilder.REQUIRED_FIELDS);

    const {
      name,
      source,
      capabilities,
      capabilityDefs,
      propsSchema,
      initialProps,
      functionsSchema,
      queryParams,
      iframeProps,
      renderTarget,
      scrolling,
      tags,
      enableHubless,
      ref,
      loadInstantly,
      initiallyOpen,
      initiallyFullscreen,
      platformName,
    } = construct;

    const factory = (params?: any): ComponentDefinition<PS, FS> => {
      const def: MutableComponentDefinition = {
        name,
        capabilities,
        capabilityDefs,
      };

      if (source.type === 'path') {
        def.path = replacePortalId(source.path);
        const resolvedQp = resolveQueryParams(queryParams, params);
        if (resolvedQp !== undefined) {
          def.queryParams = resolvedQp;
        }
      } else {
        def.src = replacePortalId(source.src);
      }

      if (propsSchema !== undefined) {
        def.propsSchema = propsSchema;
        def.initialProps = resolve(
          initialProps,
          params
        ) as typeof def.initialProps;
      }

      if (functionsSchema !== undefined) {
        def.functions = functionsSchema;
      }

      if (iframeProps !== undefined) {
        def.iframeProps = resolve(iframeProps, params);
      }

      const renderTo = resolveRenderTarget(renderTarget, params);
      if (renderTo !== undefined) {
        def.renderTo = renderTo;
      }

      if (scrolling !== undefined) {
        def.scrolling = scrolling;
      }

      if (tags !== undefined) {
        def.tags = tags as typeof def.tags;
      }

      if (enableHubless !== undefined) {
        def.enableHubless = enableHubless;
      }

      if (ref !== undefined) {
        def.ref = resolve(ref, params);
      }

      if (loadInstantly !== undefined) {
        def.deferLoading = !resolve(loadInstantly, params);
      }

      if (initiallyOpen !== undefined) {
        def.initiallyOpen = resolve(initiallyOpen, params);
      }

      if (initiallyFullscreen !== undefined) {
        def.initiallyFullscreen = initiallyFullscreen;
      }

      if (platformName !== undefined) {
        def.platformName = platformName;
      }

      return def as ComponentDefinition<PS, FS>;
    };

    const overridableDefinition = makeOverridableFactory(factory);
    const getName = () => name;
    const getPlatformName = () => platformName;
    return Object.assign(overridableDefinition, {
      getName,
      getPlatformName,
    }) as BuildResult<DefParams, PS, FS>;
  }
}
